import { useContext } from "react";
import { buildPlanningLane } from "./spotLaneFormatter";
import { UserApiContext } from "../../Components/UserDetailsContext/UserDetailsContext";

/**
 * Deduplicates planning lanes by (load_id + truck type), incrementing
 * truck_required for each duplicate instead of adding a new row.
 *
 * Extracted from NewEnquiry.mergeTrucks().
 *
 * @param {Array} lanes - Raw lane list (may contain duplicates).
 * @returns {Array} Deduplicated lane list with accurate truck_required counts.
 */
export const mergeTrucks = (lanes) => {
  const result = [];
  lanes.forEach((item) => {
    const getTruckLabel = (lane) => {
      const tt = lane.lane_details?.truck_types;
      if (!tt) return undefined;
      return Array.isArray(tt) ? tt[0]?.label : tt.label;
    };

    const existingItem = result.find(
      (el) =>
        el.load_id === item.load_id &&
        getTruckLabel(el) === getTruckLabel(item),
    );
    if (existingItem) {
      existingItem.lane_details.truck_required += 1;
    } else {
      result.push({ ...item });
    }
  });
  return result;
};

/**
 * Builds the list of lane objects from Planning module data.
 *
 * Extracted from NewEnquiry.getDatafromPlanning().
 * NOTE: The axios call for `all_users` (which sets useroption state) is intentionally
 * kept in NewEnquiry because it sets React state directly.
 *
 * Caller is responsible for:
 *  - Calling setsend_data() with the returned lanes
 *  - Calling settransporterlistempty(true) when hasEmptyTransporters is true
 *  - Calling setshow(false) when showFalse is true (all_spot_list code path)
 *
 * @param {Object} params
 * @param {Array}  params.rspl - Route/spot list from Planning.
 * @param {Array}  params.all_spot_list - All spot orders for batch planning.
 * @param {Object} params.current_truck - The currently selected truck (may be empty).
 * @param {Array}  params.tenure - The currently selected tenure option array.
 * @param {Array}  [params.spotTransporterList=[]] - User's master spot transporter list.
 * @returns {{ lanes: Array, hasEmptyTransporters: boolean, showFalse: boolean }}
 *   showFalse: true when the caller should call setshow(false) (all_spot_list path).
 */
export const buildPlanningLanes = ({
  rspl,
  all_spot_list,
  current_truck,
  tenure,
  spotTransporterList = [],
  userEmail,
}) => {
  let plist = [];
  let hasEmptyTransporters = false;
  const item = rspl && rspl.length > 0 && rspl?.[0]?.item;

  if (item?.allocated_truck?.length > 0) {
    if (item.plan_type === "spot" && item.suggested_truck?.length > 0) {
      item.suggested_truck.forEach((truck) => {
        plist.push(buildPlanningLane(item, truck, tenure, spotTransporterList, userEmail));
        hasEmptyTransporters = true;
      });
      return { lanes: plist, hasEmptyTransporters };
    } else {
      if (current_truck?.truck_type) {
        plist.push(buildPlanningLane(item, current_truck, tenure, spotTransporterList, userEmail));
        hasEmptyTransporters = true;
      } else if (!item.enquiry_floated) {
        item.allocated_truck.forEach((truck) => {
          plist.push(buildPlanningLane(item, truck, tenure, spotTransporterList, userEmail));
          hasEmptyTransporters = true;
        });
      }
      return { lanes: mergeTrucks(plist), hasEmptyTransporters };
    }
  } else if (all_spot_list?.[0]?.spot_orders?.length > 0) {
    all_spot_list[0].spot_orders.forEach((spot_order) => {
      spot_order.allocated_truck.forEach((truck) => {
        plist.push(buildPlanningLane(spot_order, truck, tenure, spotTransporterList, userEmail));
      });
    });
    return { lanes: mergeTrucks(plist), hasEmptyTransporters };
  }

  return { lanes: plist, hasEmptyTransporters };
};

/**
 * Updates the assigned user (email) for a specific truck in the planning flow.
 * Extracted from AddedLanes.handleSelectChange().
 * 
 * @param {Array}  lanes - Array of current lane objects.
 * @param {string} truckID - The truck ID to update.
 * @param {string} selectedEmail - The email of the user to assign.
 * @returns {Array} Updated array of lane objects.
 */
export const updatePlanningUserSelection = (lanes, truckID, selectedEmail) => {
  return lanes.map((enquiry) => {
    if (enquiry.truck_id === truckID) {
      return { ...enquiry, assigned_to: selectedEmail, email: selectedEmail };
    }
    return enquiry;
  });
};
